import { FC } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { CopyButton, Icon, MessageArea } from "../../../components/common";
import {
  CURRENCY_MAP,
  DEFAULT_FIAT_CURRENCY,
  INCOMING_PAYMENT_DETAILS_CONFIG,
} from "../../../constants";
import { selectSelectedCalculatorInput } from "../../../store/reducers/paymentRequestLink";
import { Currency, IncomingPaymentDetails } from "../../../types/InvoiceTypes";
import { UIStatus } from "../../../types/GlobalTypes";
import { PublicPaymentRequest } from "../../../types/PaymentRequestTypes";
import { useLocalStorage } from "../../../utils/hooks/useLocalStorage";

export const PaymentDetailsBanner: FC = () => {
  const selectedCalculatorInput = useSelector(selectSelectedCalculatorInput);

  const { data: paymentRequest } = useLocalStorage<
    Partial<PublicPaymentRequest>
  >("publicPaymentRequest");

  if (!paymentRequest) {
    return null;
  }

  const { bank_details, beneficiary_name } = paymentRequest;

  const currencyFeatures =
    CURRENCY_MAP[selectedCalculatorInput?.currency || DEFAULT_FIAT_CURRENCY]
      ?.features || [];

  const sepaText =
    selectedCalculatorInput?.currency === Currency.EUR ? (
      <>
        {" "}
        to send{" "}
        <span className="font-extrabold uppercase">
          {selectedCalculatorInput?.currency}
        </span>{" "}
        through <span className="font-extrabold">SEPA/SEPA-Instant only</span>
      </>
    ) : (
      <>
        {" "}
        to send{" "}
        <span className="font-extrabold uppercase">
          {selectedCalculatorInput?.currency}
        </span>
      </>
    );

  return (
    <MessageArea type={UIStatus.SUCCESS}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex gap-6">
            <Icon icon="Send" size={24} className="text-gray-500" disableFill />

            <div className="theme-caption text-gray-500 uppercase">
              How to make transfer
            </div>
          </div>

          <ul className="flex flex-col gap-4 list-disc list-outside pl-6">
            <li>
              Open your bank, select payment to bank recipient and paste details
              below{sepaText}
            </li>

            <li>
              Make sure you include the <span>REF#</span> in the note
            </li>
          </ul>
        </div>

        <div className="flex border-y border-green-200 py-4">
          <div className="flex flex-col gap-2 w-full">
            {currencyFeatures.map(
              (feature) =>
                bank_details &&
                bank_details[feature] && (
                  <PaymentDetailsItemByConfig
                    key={feature}
                    feature={feature}
                    value={bank_details ? bank_details[feature] : undefined}
                  />
                )
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex gap-6">
            <Icon icon="Info" size={24} className="text-gray-500" disableFill />

            <div className="theme-caption text-gray-500 uppercase">
              What is next
            </div>
          </div>

          <ul className="flex flex-col gap-4 list-disc list-outside pl-6">
            <li>
              {beneficiary_name} receives his INR within 24 hours of us
              receiving your payment
            </li>

            <li>Fees may be charged by your bank</li>
          </ul>
        </div>
      </div>
    </MessageArea>
  );
};

type PaymentDetailsItemProps = {
  label: string;
  value?: string;
  valueClassName?: string;
};

const PaymentDetailsItem: FC<PaymentDetailsItemProps> = ({
  label,
  value,
  valueClassName,
}) => {
  return (
    <div className="flex flex-col">
      <div className="flex-none uppercase theme-subheading text-sm">
        {label}
      </div>

      <div
        className={classNames(
          "theme-text flex items-center justify-between",
          valueClassName
        )}
      >
        {value || "-"}
        <CopyButton value={value} />
      </div>
    </div>
  );
};

type PaymentDetailsItemByConfigProps = {
  feature: keyof IncomingPaymentDetails;
  value?: string;
};

const PaymentDetailsItemByConfig: FC<PaymentDetailsItemByConfigProps> = ({
  feature,
  value,
}) => {
  const config = INCOMING_PAYMENT_DETAILS_CONFIG[feature];

  return (
    <PaymentDetailsItem
      label={config?.label}
      value={config?.format ? config?.format(value) : value}
      valueClassName={config?.valueClassName}
    />
  );
};
